import { useEffect, useState } from "react";
import { Col, Nav, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { IconAnniversary } from "../../common/icons";
import { LoadingContainer } from "../../common/loaders/loadingcontainer";
import { capitalizeFirstLetter, localized, nextBirthDay } from "../../common/utils";
import { chartAnniversaryQuery } from "../../api";

const localStorageKey = "dashboardAnniversary";
const milestoneStyle = { fontWeight: 600 };

const compareFn = (a, b) => a.anniversaryDate - b.anniversaryDate;


export function Anniversaries({ cardProps, changeProperty }) {
  const { t } = useTranslation();

  const birthdayEvent = anniversaryPerson => {
    const age = anniversaryPerson.age + 1;
    if (age % 10 === 0) anniversaryPerson.milestone = true;
    anniversaryPerson.bday = true;
    anniversaryPerson.anniversaryTitle = t("birthdayAgeYears", "syntymäpäivä {{age}} vuotta", { age });
    return anniversaryPerson;
  };

  const startDateEvent = anniversaryPerson => {
    const years = anniversaryPerson.experienceYears + 1;
    if (years > 9 && years % 5 === 0) anniversaryPerson.milestone = true;
    anniversaryPerson.anniversaryTitle = t("yearsInHouse", "talossa {{years}} vuotta", { years });
    return anniversaryPerson;
  };


  const { data } = useQuery(chartAnniversaryQuery);
  const [anniversaryDuration, setAnniversaryDuration] = useState(parseInt(localStorage.getItem(localStorageKey)) || 1);

  const { resolvedLanguage } = localized;
  const localizedMonth = new Intl.DateTimeFormat(resolvedLanguage, { month: "long" });
  const rtf = new Intl.RelativeTimeFormat(resolvedLanguage, { style: "long", numeric: "auto" });



  const now = new Date();
  now.setUTCHours(0, 0, 0, 0); // midnight

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  // Get Monday of the current week
  const startOfThisWeek = new Date(now);
  startOfThisWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Adjust to Monday

  const endOfThisWeek = new Date(startOfThisWeek);
  endOfThisWeek.setDate(startOfThisWeek.getDate() + 6); // Sunday of this week

  const startOfNextWeek = new Date(startOfThisWeek);
  startOfNextWeek.setDate(startOfNextWeek.getDate() + 7); // Next Monday

  const endOfNextWeek = new Date(startOfNextWeek);
  endOfNextWeek.setDate(startOfNextWeek.getDate() + 6); // Next Sunday


  const [list, setList] = useState();
  useEffect(() => {
    if (data) setAnniversaries(anniversaryDuration);
  }, [data]);


  function checkFutureEvent(event, anniversaryPerson) {
    if (event === "birthDate" && new Date(anniversaryPerson[event]) > now) --anniversaryPerson.age;
    if (event === "startDate" && new Date(anniversaryPerson[event]) > now) --anniversaryPerson.experienceYears;
  }


  function setAnniversaries(anniversaryDuration) {
    const anniversaries = {
      yesterday: [],
      today: [],
      tomorrow: [],
      thisWeek: [],
      nextWeek: [],
      0: [], // current month
      1: [], // current month + 1
      2: [], // current month + 2
      3: [],
      4: [],
      5: [],
    };


    for (const person of data.data) {
      const events = ["birthDate", "startDate"];
      for (const event of events) {

        //Skip future start dates for employments to prevent 0 values
        if (event === "startDate" && new Date(person.startDate) >= now) {
          continue;
        }

        const anniversaryPerson = {
          ...person,
          anniversaryDate: new Date(nextBirthDay(now, person[event])),
        };

        let category = "";

        if (anniversaryPerson.anniversaryDate.getMonth() === yesterday.getMonth() && anniversaryPerson.anniversaryDate.getDate() === yesterday.getDate()) {
          if (event === "birthDate") --anniversaryPerson.age;
          if (event === "startDate") --anniversaryPerson.experienceYears;
          anniversaryPerson.anniversaryDate = yesterday;
          category = "yesterday";

        } else if (anniversaryPerson.anniversaryDate.getMonth() === now.getMonth() && anniversaryPerson.anniversaryDate.getDate() === now.getDate()) {
          if (event === "birthDate") --anniversaryPerson.age;
          if (event === "startDate") --anniversaryPerson.experienceYears;
          category = "today";

        } else if (anniversaryPerson.anniversaryDate.getMonth() === tomorrow.getMonth() && anniversaryPerson.anniversaryDate.getDate() === tomorrow.getDate()) {
          checkFutureEvent(event, anniversaryPerson);
          category = "tomorrow";

        } else if (anniversaryPerson.anniversaryDate >= startOfThisWeek && anniversaryPerson.anniversaryDate <= endOfThisWeek) {
          checkFutureEvent(event, anniversaryPerson);
          category = "thisWeek";

        } else if (anniversaryPerson.anniversaryDate >= startOfNextWeek && anniversaryPerson.anniversaryDate <= endOfNextWeek) {
          checkFutureEvent(event, anniversaryPerson);
          category = "nextWeek";
        }

        else {
          const nextMonth = new Date(now);
          for (let i = 0; i < anniversaryDuration; ++i) {

            if (anniversaryPerson.anniversaryDate.getFullYear() === nextMonth.getFullYear() && anniversaryPerson.anniversaryDate.getMonth() === nextMonth.getMonth()) {
              checkFutureEvent(event, anniversaryPerson);
              category = i;
              if (!anniversaries[i].monthName) anniversaries[i].monthName = capitalizeFirstLetter(localizedMonth.format(nextMonth));
            }

            nextMonth.setMonth(nextMonth.getMonth() + 1);
          }
        }

        let result =
          event === "birthDate"
            ? birthdayEvent(anniversaryPerson)
            : startDateEvent(anniversaryPerson);

        //Prevent 0 values
        if (event === "startDate" && anniversaryPerson.experienceYears + 1 <= 0) {
          continue;
        }

        if (category !== "" && result) {
          anniversaries[category].push(result);
        }
      }
    }

    for (const category of Object.keys(anniversaries)) {
      if (anniversaries[category].length) {
        anniversaries[category].sort(compareFn);
      }
    }

    setList(anniversaries);
    localStorage.setItem(localStorageKey, anniversaryDuration);
    changeProperty("duration", anniversaryDuration);
    setAnniversaryDuration(anniversaryDuration);
  }

  const categoryTitle = category => {
    switch (category) {
      case "yesterday": return rtf.format(-1, "day");
      case "today": return rtf.format(0, "day");
      case "tomorrow": return rtf.format(1, "day");
      case "thisWeek": return rtf.format(0, "week");
      case "nextWeek": return rtf.format(1, "week");
      default: return rtf.format(0, "month");
    }
  };

  return list
    ?
    <Row className="gap-3 align-items-center">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Nav.Link as={Link} to="/stats/anniversaries">
          <h4 style={{ color: "var(--heeros-blue)" }}>{t("Anniversaries", "Merkkipäivät")}</h4>
        </Nav.Link>
        <label style={{ marginRight: "2em" }} htmlFor="periodSelect">{`${t("Select", "Valitse")} ${t("period", "Ajanjakso").toLocaleLowerCase()}`} <select
          id="periodSelect"
          onChange={e => setAnniversaries(parseInt(e.target.value))}
          value={anniversaryDuration}
        >{(() => {
          const arr = [];
          for (let i = 1; i < 7; ++i) {
            arr.push(<option key={i} value={i}>{`${i} ${t("mo", "kk")}`}</option>);
          }
          return arr;
        })()}
        </select></label>
      </div>

      <Row className="gap-3 align-items-center" style={{ maxHeight: "500px", overflowY: "auto" }}>
        {["yesterday", "today", "tomorrow", "thisWeek", "nextWeek"].map(category => list[category].length > 0 && <div key={category}>
          <b>{capitalizeFirstLetter(categoryTitle(category))}</b>
          {list[category].map((person, idx) => <Row key={idx} style={person.milestone && milestoneStyle}>
            <Col className="flex-grow-0 align-self-center"><IconAnniversary bgColor={!person.bday && "#B7A0E0"} /></Col>
            <Col>
              <Row><Col>{person.name}</Col></Row>
              <Row>
                <Col>{person.anniversaryTitle}</Col>
                <Col className="flex-grow-0">{person.anniversaryDate.toLocaleDateString(resolvedLanguage)}</Col>
              </Row>
            </Col>
          </Row>)}
          <hr />
        </div>)}

        {[0, 1, 2, 3, 4, 5].map(i => list[i].length > 0 && <div key={i}>
          <b>{i ? list[i].monthName : capitalizeFirstLetter(categoryTitle())}</b>
          {list[i].map((person, idx) => <Row key={idx} style={person.milestone && milestoneStyle}>
            <Col><IconAnniversary size="16" bgColor={!person.bday && "#B7A0E0"} /> {person.name}, {person.anniversaryTitle}</Col>
            <Col className="flex-grow-0">{person.anniversaryDate.toLocaleDateString(resolvedLanguage)}</Col>
          </Row>)}
        </div>)}

      </Row>
    </Row>
    : <LoadingContainer />;
}
